import { useEffect, useRef } from "react";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { Capacitor } from "@capacitor/core";
import {
  db,
  getRemoteConfigValue,
  getRemoteConfigValueSource,
  logEvent,
  logUserProperty,
  manageJob,
  newId,
  remoteConfigReady,
  storage,
  type RemoteConfigValueSource,
} from "@um/ui";
import { getDocument } from "@um/ui";
import {
  HandleJobTask,
  JobAsyncResult,
  type Account,
  type AccountfeedbackStatsState,
  type AccountRole,
  type AccountSettings,
  type Activity,
  type CancellationReason,
  type Feedback,
  type FeedbackContentContext,
  type FeedbackReportCategory,
} from "@um/contracts";
import { useAuthStore } from "@um/ui";
import { totalEngagementPoints } from "@/services/accountPoints";
import { useAutoUpdateStore } from "@/store/useAutoUpdateStore";

// ─── Conditions for when the feedback popup is shown ───────────────────────

/** When to consider showing: after an activity completion vs once on app open. */
export type FeedbackTrigger = "after_activity" | "on_app_open";

export interface FeedbackVariantConfig {
  pointsThreshold: number;
  requireOneWeek: boolean;
  trigger: FeedbackTrigger;
}

/** Variant IDs for Firebase A/B test "FeedbackStrategy" parameter (use these exact strings). */
export const FEEDBACK_VARIANT_AFTER_ACTIVITY_15 = "feedback_after_activity_15";
export const FEEDBACK_VARIANT_AFTER_ACTIVITY_15_1WEEK =
  "feedback_after_activity_15_1week";
export const FEEDBACK_VARIANT_AFTER_ACTIVITY_30 = "feedback_after_activity_30";
export const FEEDBACK_VARIANT_AFTER_ACTIVITY_30_1WEEK =
  "feedback_after_activity_30_1week";
export const FEEDBACK_VARIANT_ON_APP_OPEN_15 = "feedback_on_app_open_15";
export const FEEDBACK_VARIANT_ON_APP_OPEN_15_1WEEK =
  "feedback_on_app_open_15_1week";
export const FEEDBACK_VARIANT_ON_APP_OPEN_30 = "feedback_on_app_open_30";
export const FEEDBACK_VARIANT_ON_APP_OPEN_30_1WEEK =
  "feedback_on_app_open_30_1week";

export type FeedbackVariantId =
  | typeof FEEDBACK_VARIANT_AFTER_ACTIVITY_15
  | typeof FEEDBACK_VARIANT_AFTER_ACTIVITY_15_1WEEK
  | typeof FEEDBACK_VARIANT_AFTER_ACTIVITY_30
  | typeof FEEDBACK_VARIANT_AFTER_ACTIVITY_30_1WEEK
  | typeof FEEDBACK_VARIANT_ON_APP_OPEN_15
  | typeof FEEDBACK_VARIANT_ON_APP_OPEN_15_1WEEK
  | typeof FEEDBACK_VARIANT_ON_APP_OPEN_30
  | typeof FEEDBACK_VARIANT_ON_APP_OPEN_30_1WEEK;

const VARIANT_CONFIG: Record<FeedbackVariantId, FeedbackVariantConfig> = {
  [FEEDBACK_VARIANT_AFTER_ACTIVITY_15]: {
    pointsThreshold: 15,
    requireOneWeek: false,
    trigger: "after_activity",
  },
  [FEEDBACK_VARIANT_AFTER_ACTIVITY_15_1WEEK]: {
    pointsThreshold: 15,
    requireOneWeek: true,
    trigger: "after_activity",
  },
  [FEEDBACK_VARIANT_AFTER_ACTIVITY_30]: {
    pointsThreshold: 30,
    requireOneWeek: false,
    trigger: "after_activity",
  },
  [FEEDBACK_VARIANT_AFTER_ACTIVITY_30_1WEEK]: {
    pointsThreshold: 30,
    requireOneWeek: true,
    trigger: "after_activity",
  },
  [FEEDBACK_VARIANT_ON_APP_OPEN_15]: {
    pointsThreshold: 15,
    requireOneWeek: false,
    trigger: "on_app_open",
  },
  [FEEDBACK_VARIANT_ON_APP_OPEN_15_1WEEK]: {
    pointsThreshold: 15,
    requireOneWeek: true,
    trigger: "on_app_open",
  },
  [FEEDBACK_VARIANT_ON_APP_OPEN_30]: {
    pointsThreshold: 30,
    requireOneWeek: false,
    trigger: "on_app_open",
  },
  [FEEDBACK_VARIANT_ON_APP_OPEN_30_1WEEK]: {
    pointsThreshold: 30,
    requireOneWeek: true,
    trigger: "on_app_open",
  },
};

/** Get config for a variant ID (from Firebase "FeedbackStrategy" parameter). */
export function getFeedbackVariantConfig(
  variantId: string | null | undefined
): FeedbackVariantConfig | null {
  if (!variantId) return null;
  return VARIANT_CONFIG[variantId as FeedbackVariantId] ?? null;
}

/** Current experiment variant from Remote Config "FeedbackStrategy" parameter. */
export function getFeedbackExperimentVariant(): string | null {
  const condition = getRemoteConfigValue("FeedbackStrategy") as string;
  return condition?.trim() || null;
}

// ─── FeedbackStrategy observability ────────────────────────────────────────
//
// The Remote Config `FeedbackStrategy` parameter drives the entire in-app
// review flow (eligibility threshold, trigger, 1-week gate). We have repeatedly
// had to ask "is this user actually on a variant? is it the default? did
// fetchAndActivate even succeed?" — so this section emits structured logs and
// analytics events so we can answer that from GA/Sentry without code archaeology.

export type FeedbackStrategySnapshot = {
  /** Raw Remote Config value (post-trim), e.g. `"feedback_after_activity_15"`. */
  raw: string | null;
  /** Where the value came from (remote / default / env override / absent). */
  source: RemoteConfigValueSource;
  /** Variant id when `raw` parses to a known variant; otherwise null. */
  variantId: FeedbackVariantId | null;
  /** Parsed config (threshold/trigger/requireOneWeek) or null when raw is unrecognized. */
  variantConfig: FeedbackVariantConfig | null;
  /** True when `variantConfig` is non-null (variant string was recognized). */
  isParsed: boolean;
};

/**
 * Read the current FeedbackStrategy state (raw RC value + source + parsed variant).
 *
 * Cheap to call — does not hit the network. Result depends on whether Remote
 * Config's `fetchAndActivate` has already settled (see `remoteConfigReady`).
 */
export function getFeedbackStrategySnapshot(): FeedbackStrategySnapshot {
  const raw = getFeedbackExperimentVariant();
  const source = getRemoteConfigValueSource("FeedbackStrategy");
  const variantConfig = getFeedbackVariantConfig(raw);
  const variantId = variantConfig ? (raw as FeedbackVariantId) : null;
  return {
    raw,
    source,
    variantId,
    variantConfig,
    isParsed: !!variantConfig,
  };
}

/** GA4 user property values are capped at 36 chars. */
const GA_USER_PROPERTY_MAX = 36;

function clampForUserProperty(value: string): string {
  return value.length > GA_USER_PROPERTY_MAX
    ? value.slice(0, GA_USER_PROPERTY_MAX)
    : value;
}

/**
 * Dedupe set so we don't spam analytics on every render / hook re-run.
 * Keyed by `${phase}:${userId}` — Remote Config assignments are per Firebase
 * installation (≈ per signed-in user), NOT per account, so we must dedupe at
 * the user level. Multiple users on the same shared account can be on
 * different FeedbackStrategy variants and must each emit their own snapshot.
 */
const emittedFeedbackStrategyKeys = new Set<string>();

function emitFeedbackStrategyObservability(
  userId: string,
  accountId: string | null,
  phase: "initial" | "post_activation"
): void {
  const dedupeKey = `${phase}:${userId}`;
  if (emittedFeedbackStrategyKeys.has(dedupeKey)) return;
  emittedFeedbackStrategyKeys.add(dedupeKey);

  const snapshot = getFeedbackStrategySnapshot();

  console.log(
    `[feedback] FeedbackStrategy snapshot — phase=${phase} userId=${userId} accountId=${
      accountId ?? "(none)"
    } raw=${JSON.stringify(snapshot.raw)} source=${snapshot.source} variantId=${
      snapshot.variantId ?? "(unparsed)"
    } trigger=${snapshot.variantConfig?.trigger ?? "(none)"} pointsThreshold=${
      snapshot.variantConfig?.pointsThreshold ?? "(none)"
    } requireOneWeek=${snapshot.variantConfig?.requireOneWeek ?? "(none)"}`
  );

  logEvent("um_feedback_strategy_resolved", {
    phase,
    // Primary dimension: user (this is who Remote Config assigned a variant to).
    userId,
    // Secondary dimension: account (shared resource; useful for funnel joins
    // with account-scoped events like um_review_request_enqueued, but NOT
    // the unit of variant assignment).
    accountId: accountId ?? undefined,
    raw: snapshot.raw ?? "(none)",
    source: snapshot.source,
    variantId: snapshot.variantId ?? "(unparsed)",
    parsed: snapshot.isParsed,
    trigger: snapshot.variantConfig?.trigger ?? "(none)",
    pointsThreshold: snapshot.variantConfig?.pointsThreshold,
    requireOneWeek: snapshot.variantConfig?.requireOneWeek,
  });

  // GA user properties (per Firebase user — exactly the right scope for
  // experiment assignment). Lets the GA audience builder slice users by
  // variant without joining to a separate event.
  logUserProperty(
    "FeedbackStrategy",
    clampForUserProperty(snapshot.raw ?? "(none)")
  );
  logUserProperty(
    "FeedbackStrategySource",
    clampForUserProperty(snapshot.source)
  );
  logUserProperty(
    "FeedbackStrategyTrigger",
    clampForUserProperty(snapshot.variantConfig?.trigger ?? "(none)")
  );
}

/**
 * Subscribes the current **user** (not account) to FeedbackStrategy telemetry.
 * Remote Config variant assignment is per Firebase installation, so two users
 * sharing one account can land on different variants — each must be tracked
 * separately. Emits two `um_feedback_strategy_resolved` events per user:
 *
 *   1. `initial` — fired immediately on first mount. May be `source=default`
 *      if Remote Config hasn't activated yet (this is the window every
 *      caller of `getRemoteConfigValue` runs in until ~250-1500ms after boot).
 *   2. `post_activation` — fired once `remoteConfigReady` resolves, so we
 *      can see what value the user actually ended up with after the network
 *      round-trip. Compare to `initial` to detect "stuck on default" cases.
 *
 * Also sets GA user properties (`FeedbackStrategy`, `FeedbackStrategySource`,
 * `FeedbackStrategyTrigger`) on the current Firebase user so the GA audience
 * builder can slice users by variant without joining to a separate event.
 *
 * @param userId    Firebase auth UID — the unit of variant assignment.
 * @param accountId Currently selected account id, attached as a secondary
 *                  dimension on the event (useful for funnel joins, not the
 *                  primary key).
 */
export function useFeedbackStrategyObservability(
  userId: string | null | undefined,
  accountId: string | null
): void {
  useEffect(() => {
    if (!userId) return;
    emitFeedbackStrategyObservability(userId, accountId, "initial");

    let cancelled = false;
    remoteConfigReady.then((status) => {
      if (cancelled) return;
      console.log(
        `[feedback] RemoteConfig ready — userId=${userId} activated=${status.activated} durationMs=${status.durationMs}${
          status.error ? ` error=${String(status.error)}` : ""
        }`
      );
      emitFeedbackStrategyObservability(userId, accountId, "post_activation");
    });

    return () => {
      cancelled = true;
    };
  }, [userId, accountId]);
}

// ─── Show conditions (thresholds, cooldowns, etc.) ─────────────────────────

/** Default points threshold when no experiment variant is set. */
export const FEEDBACK_POINTS_THRESHOLD_DEFAULT = 15;

/** After "Maybe later", don't show again for this many days. */
export const FEEDBACK_DISMISS_COOLDOWN_DAYS = 30;

/** Maximum number of times to show the feedback popup per account (then never again). */
export const FEEDBACK_MAX_SHOW_COUNT = 3;

/** Minimum account age in days for "after 1 week" variants. */
export const FEEDBACK_MIN_AGE_DAYS_FOR_1WEEK = 7;

/** Account types that should never see the feedback popup (e.g. bots, anonymous). */
export const FEEDBACK_EXCLUDED_ACCOUNT_TYPES = ["bot", "anonymous"] as const;

/** Prompt state lives on `Account/{id}/Settings/account` (same subscription as other account settings). */
function getAccountSettingsFeedbackRef(accountId: string) {
  return doc(db, "Account", accountId, "Settings", "account");
}

function getfeedbackStatsState(
  accountSettings: AccountSettings | null | undefined
): AccountfeedbackStatsState | undefined {
  return accountSettings?.feedbackStats;
}

function timestampMs(t: Timestamp | undefined): number | null {
  if (!t?.toMillis) return null;
  try {
    return t.toMillis();
  } catch {
    return null;
  }
}

/**
 * After manageJob successfully enqueues createReviewRequest: bump show count and
 * last-queued time on `Account/{id}/Settings/account.feedbackStats`.
 */
async function recordReviewRequestActivityQueued(
  accountId: string
): Promise<void> {
  try {
    await updateDoc(getAccountSettingsFeedbackRef(accountId), {
      "feedbackStats.reviewPromptShowCount": increment(1),
      "feedbackStats.lastReviewActivityQueued": serverTimestamp(),
    });
  } catch (e) {
    console.warn(
      "[feedback] Failed to update Settings/account feedbackStats after review enqueue",
      e
    );
  }
}

export type FeedbackShowContext = {
  accountId: string | null;
  /** Account snapshot (engagement points, type, created). */
  account?: Account | null;
  /** `Account/{id}/Settings/account` snapshot; eligibility reads `feedbackStats` here. */
  accountSettings?: AccountSettings | null;
  totalPoints: number;
  accountType?: string;
  /** Effective account start: `AccountSettings.migrated` if set, else `Account.created` (for "after 1 week" check). */
  accountCreatedAt?: Timestamp | null;
  /** Current trigger: app open vs after activity (must match variant.trigger). */
  triggerSource: FeedbackTrigger;
  /** Experiment variant config; if null, default threshold and no 1-week check. */
  variantConfig?: FeedbackVariantConfig | null;
};

export function buildFeedbackShowContext(
  accountId: string | null,
  account: Account | null | undefined,
  triggerSource: FeedbackTrigger,
  accountSettings?: AccountSettings | null
): FeedbackShowContext {
  return {
    accountId,
    account: account ?? null,
    accountSettings: accountSettings ?? null,
    totalPoints: totalEngagementPoints(account?.stats?.engagementPoints),
    accountType: account?.type,
    accountCreatedAt:
      accountSettings?.migrated !== undefined
        ? accountSettings.migrated
        : (account?.created ?? null),
    triggerSource,
    variantConfig: getFeedbackVariantConfig(getFeedbackExperimentVariant()),
  };
}

export interface FeedbackSubmission {
  rating: number;
  feedback?: string;
}

/**
 * Enqueue the backend review-request activity without waiting for completion.
 * This is used by experiment-driven triggers (e.g. on_app_open) where we only
 * need the job to be started, not the final task status.
 *
 * Emits `um_review_request_enqueue_attempt` / `..._enqueued` / `..._failed`
 * so we can attribute the "asked to review" funnel to the active
 * FeedbackStrategy variant.
 */
export async function enqueueReviewRequestJob(
  accountId: string
): Promise<JobAsyncResult> {
  if (!accountId) {
    throw new Error("Missing accountId for enqueueReviewRequestJob.");
  }

  const snapshot = getFeedbackStrategySnapshot();
  // FeedbackStrategy is assigned per Firebase user, but the review-request
  // activity is queued on an account (which may be shared). Capture both so
  // we can attribute the funnel correctly: which user's variant triggered
  // the queue for which account.
  const triggeringUserId = useAuthStore.getState().user?.uid;
  const analyticsBase = {
    accountId,
    userId: triggeringUserId,
    feedbackStrategy: snapshot.raw ?? "(none)",
    feedbackStrategySource: snapshot.source,
    variantId: snapshot.variantId ?? "(unparsed)",
    trigger: snapshot.variantConfig?.trigger ?? "(none)",
  };

  console.log(
    `[feedback] enqueueReviewRequestJob attempt — userId=${
      triggeringUserId ?? "(none)"
    } accountId=${accountId} variant=${
      snapshot.variantId ?? "(unparsed)"
    } source=${snapshot.source}`
  );
  logEvent("um_review_request_enqueue_attempt", analyticsBase);

  try {
    // Backend requires this pre-existing job document.
    const accountManagerJobRef = getDocument(
      `Account/${accountId}/Job/account-manager`
    );
    const accountManagerJobSnap = await getDoc(accountManagerJobRef);
    if (!accountManagerJobSnap.exists()) {
      throw new Error(
        "Missing Account/{accountId}/Job/account-manager. Create that job first."
      );
    }

    const result = await manageJob({
      type: "action",
      accountId,
      jobId: "account-manager",
      actionId: "createReviewRequest",
    });

    const data = result.data as JobAsyncResult;
    if (!data.handleJobTaskId) {
      throw new Error(
        `manageJob returned no handleJobTaskId (jobId: ${data.jobId ?? "unknown"}).`
      );
    }

    await recordReviewRequestActivityQueued(accountId);

    logEvent("um_review_request_enqueued", {
      ...analyticsBase,
      handleJobTaskId: data.handleJobTaskId,
      jobId: data.jobId,
    });
    return data;
  } catch (e) {
    logEvent("um_review_request_enqueue_failed", {
      ...analyticsBase,
      errorMessage: e instanceof Error ? e.message : String(e),
    });
    throw e;
  }
}

/**
 * Enqueue backend job to create a review-request activity.
 * This uses accountManager/createReviewRequest.
 */
export async function createReviewRequestJob(accountId: string): Promise<{
  jobId: string;
  handleJobTaskId?: string;
  handleJobTaskStatus?: string;
  handleJobTaskLastError?: string;
}> {
  if (!accountId) {
    throw new Error("Missing accountId for createReviewRequest.");
  }

  // Backend requires this pre-existing job document.
  const accountManagerJobRef = getDocument(
    `Account/${accountId}/Job/account-manager`
  );
  const accountManagerJobSnap = await getDoc(accountManagerJobRef);
  if (!accountManagerJobSnap.exists()) {
    throw new Error(
      "Missing Account/{accountId}/Job/account-manager. Create that job first."
    );
  }

  const result = await manageJob({
    type: "action",
    accountId,
    jobId: "account-manager",
    actionId: "createReviewRequest",
  });
  const data = result.data as JobAsyncResult;
  if (!data.handleJobTaskId) {
    throw new Error(
      `manageJob returned no handleJobTaskId (jobId: ${data.jobId ?? "unknown"}).`
    );
  }

  await recordReviewRequestActivityQueued(accountId);

  // Wait for task execution result (manageJob only enqueues).
  const taskRef = getDocument<HandleJobTask>(
    `Account/${accountId}/HandleJobTask/${data.handleJobTaskId}`
  );
  for (let attempt = 0; attempt < 60; attempt++) {
    const snap = await getDoc(taskRef);
    if (snap.exists()) {
      const task = snap.data() as {
        status?: "queued" | "processing" | "completed" | "failed" | "cancelled";
        lastError?: string;
      };
      if (
        task.status === "completed" ||
        task.status === "failed" ||
        task.status === "cancelled"
      ) {
        return {
          ...data,
          handleJobTaskStatus: task.status,
          handleJobTaskLastError: task.lastError,
        };
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  throw new Error(
    `Task ${data.handleJobTaskId} did not complete within timeout.`
  );
}

/**
 * Persist user feedback to Firestore (Account/{accountId}/Feedback) and mark submitted on Settings/account.
 */
export async function submitFeedback(
  accountId: string,
  data: FeedbackSubmission
): Promise<void> {
  if (!accountId) return;
  const feedbackRef = collection(db, "Account", accountId, "Feedback");
  await addDoc(feedbackRef, {
    rating: data.rating,
    ...(data.feedback?.trim() && { feedback: data.feedback.trim() }),
    created: serverTimestamp(),
  });

  try {
    await updateDoc(getAccountSettingsFeedbackRef(accountId), {
      "feedbackStats.feedbackSubmittedAt": serverTimestamp(),
    });
  } catch (e) {
    console.warn(
      "[feedback] Failed to record feedbackSubmittedAt on Settings/account",
      e
    );
  }
}

// ─── User-initiated page reports ────────────────────────────────────────────

export interface PageReportScreenshot {
  dataUrl: string;
  width: number;
  height: number;
}

export interface PageReportSubmission {
  message: string;
  /** Category chosen in the report drawer (reports need a category or a message). */
  category?: FeedbackReportCategory;
  /** Specific content being reported (e.g. an AI-generated character). */
  content?: FeedbackContentContext;
  /** Page path at the time the report was opened, e.g. `/characters`. */
  pagePath: string;
  pageTitle?: string;
  screenshot?: PageReportScreenshot | null;
}

async function uploadReportScreenshot(
  accountId: string,
  feedbackId: string,
  screenshot: PageReportScreenshot
): Promise<Feedback["screenshot"] | undefined> {
  try {
    const blob = await (await fetch(screenshot.dataUrl)).blob();
    const storagePath = `Account/${accountId}/Feedback/${feedbackId}.jpg`;
    const fileRef = storageRef(storage, storagePath);
    await uploadBytes(fileRef, blob, { contentType: "image/jpeg" });
    const url = await getDownloadURL(fileRef);
    return {
      storagePath,
      url,
      width: screenshot.width,
      height: screenshot.height,
    };
  } catch (e) {
    // Screenshot is best effort — never block the report on it.
    console.warn("[feedback] Failed to upload report screenshot", e);
    return undefined;
  }
}

/**
 * Persist a user-initiated report to `Account/{accountId}/Feedback`,
 * including page context, device info, an optional screenshot uploaded to
 * Storage at `Account/{accountId}/Feedback/{id}.jpg`, and — for content
 * reports (Google Play AI-Generated Content policy) — the reported content's
 * context and a category so reports can inform filtering and moderation.
 */
export async function submitPageReport(
  accountId: string,
  report: PageReportSubmission
): Promise<void> {
  if (!accountId) {
    throw new Error("Missing accountId for submitPageReport.");
  }
  const message = report.message.trim();
  if (!message && !report.category) {
    throw new Error("Report needs a category or a message.");
  }

  const { user, userData } = useAuthStore.getState();
  const feedbackId = newId();

  const screenshot = report.screenshot
    ? await uploadReportScreenshot(accountId, feedbackId, report.screenshot)
    : undefined;

  const appVersion = useAutoUpdateStore.getState().appVersion?.versionName;

  // `db` is initialized with ignoreUndefinedProperties, so optional fields
  // that are undefined are simply omitted from the document.
  await setDoc(doc(db, "Account", accountId, "Feedback", feedbackId), {
    type: "report",
    message: message || undefined,
    category: report.category,
    content: report.content,
    status: "new",
    user: userData?.ref,
    userId: user?.uid,
    userEmail: userData?.email ?? user?.email ?? undefined,
    accountId,
    page: {
      path: report.pagePath,
      title: report.pageTitle,
      url: window.location.href,
    },
    screenshot,
    device: {
      userAgent: navigator.userAgent,
      platform: Capacitor.getPlatform(),
      appVersion,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    },
    created: serverTimestamp(),
  });

  logEvent("um_report_page_submitted", {
    accountId,
    userId: user?.uid,
    path: report.pagePath,
    hasScreenshot: !!screenshot,
    ...(report.category ? { category: report.category } : {}),
    ...(report.content ? { contentType: report.content.type } : {}),
  });
}

// ─── Support & feedback requests ────────────────────────────────────────────

/**
 * Persist a support/feedback request from the profile menu's "Support &
 * Feedback" drawer to `Account/{accountId}/Feedback` (`type: "support"`).
 * The backend `onCreateFeedback` trigger emails support@uniqueminds.com so
 * the team is alerted without watching the admin grid.
 */
export async function submitSupportRequest(
  accountId: string,
  message: string
): Promise<void> {
  if (!accountId) {
    throw new Error("Missing accountId for submitSupportRequest.");
  }
  const trimmed = message.trim();
  if (!trimmed) {
    throw new Error("Support request needs a message.");
  }

  const { user, userData } = useAuthStore.getState();
  const feedbackId = newId();
  const appVersion = useAutoUpdateStore.getState().appVersion?.versionName;
  const pagePath = window.location.pathname;

  // `db` is initialized with ignoreUndefinedProperties, so optional fields
  // that are undefined are simply omitted from the document.
  await setDoc(doc(db, "Account", accountId, "Feedback", feedbackId), {
    type: "support",
    message: trimmed,
    status: "new",
    user: userData?.ref,
    userId: user?.uid,
    userEmail: userData?.email ?? user?.email ?? undefined,
    accountId,
    page: {
      path: pagePath,
      url: window.location.href,
    },
    device: {
      userAgent: navigator.userAgent,
      platform: Capacitor.getPlatform(),
      appVersion,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    },
    created: serverTimestamp(),
  });

  logEvent("um_support_request_submitted", {
    accountId,
    userId: user?.uid,
    path: pagePath,
  });
}

// ─── Cancellation exit survey ───────────────────────────────────────────────

export interface CancellationSurveySubmission {
  reason?: CancellationReason;
  message?: string;
  /** Subscription the survey is about (from the non-renewing snapshot). */
  entitlementId?: string;
  productIdentifier?: string;
}

/**
 * Persist an exit-survey answer to `Account/{accountId}/Feedback` with
 * `type: "cancellation"` so it lands in the same admin grid as reports and
 * ratings. Mirrors the win-back email survey (`via: "email"`) written by the
 * retention backend.
 */
export async function submitCancellationFeedback(
  accountId: string,
  submission: CancellationSurveySubmission
): Promise<void> {
  if (!accountId) {
    throw new Error("Missing accountId for submitCancellationFeedback.");
  }
  const message = submission.message?.trim();
  if (!submission.reason && !message) {
    throw new Error("Cancellation feedback needs a reason or a message.");
  }

  const { user, userData } = useAuthStore.getState();
  const appVersion = useAutoUpdateStore.getState().appVersion?.versionName;

  await setDoc(doc(db, "Account", accountId, "Feedback", newId()), {
    type: "cancellation",
    cancellationReason: submission.reason,
    message: message || undefined,
    via: "app",
    status: "new",
    user: userData?.ref,
    userId: user?.uid,
    userEmail: userData?.email ?? user?.email ?? undefined,
    accountId,
    device: {
      userAgent: navigator.userAgent,
      platform: Capacitor.getPlatform(),
      appVersion,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    },
    created: serverTimestamp(),
  });

  logEvent("um_cancel_survey_submitted", {
    accountId,
    userId: user?.uid,
    ...(submission.reason ? { reason: submission.reason } : {}),
    hasMessage: !!message,
    ...(submission.entitlementId
      ? { entitlementId: submission.entitlementId }
      : {}),
    ...(submission.productIdentifier
      ? { productIdentifier: submission.productIdentifier }
      : {}),
  });
}

/**
 * Dental pilot prompts.
 *
 * Two writers, deliberately different in destination. The sentiment prompt is a
 * `Feedback` doc like every other rating; the weekly check-in appends to the
 * MIND, because it is a per-person longitudinal series rather than a message to
 * the team — the point is whether "needs less help" moves over the weeks.
 */

export interface DentalFeedbackSubmission {
  rating: number;
  message?: string;
  /** The rotating question actually asked, so the answer stays interpretable. */
  questionPrompt: string;
  careTypes: string[];
  careLabels: string[];
  mindId: string;
  mindName: string;
  completedByRole: string;
}

export async function submitDentalFeedback(
  accountId: string,
  submission: DentalFeedbackSubmission
): Promise<void> {
  if (!accountId) {
    throw new Error("Missing accountId for submitDentalFeedback.");
  }
  if (!submission.rating) {
    throw new Error("Dental feedback needs a rating.");
  }
  const message = submission.message?.trim();

  const { user, userData } = useAuthStore.getState();
  const appVersion = useAutoUpdateStore.getState().appVersion?.versionName;

  await setDoc(doc(db, "Account", accountId, "Feedback", newId()), {
    // Its own type rather than "rating", so pilot sentiment can be read apart
    // from general app ratings in the admin.
    type: "dental",
    rating: submission.rating,
    message: message || undefined,
    status: "new",
    via: "app",
    user: userData?.ref,
    userId: user?.uid,
    userEmail: userData?.email ?? user?.email ?? undefined,
    accountId,
    // The real route. Arjun hardcoded "/dental-hub", a surface that no longer
    // exists, which made every doc claim to come from a page nobody can visit.
    page: {
      path: window.location.pathname,
      title: document.title,
      url: window.location.href,
    },
    device: {
      userAgent: navigator.userAgent,
      platform: Capacitor.getPlatform(),
      appVersion,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    },
    dentalContext: {
      careTypes: submission.careTypes,
      careLabels: submission.careLabels,
      questionPrompt: submission.questionPrompt,
      mindId: submission.mindId,
      mindName: submission.mindName,
      completedByRole: submission.completedByRole,
    },
    created: serverTimestamp(),
  });

  logEvent("um_dental_feedback_submitted", {
    accountId,
    userId: user?.uid,
    rating: submission.rating,
    hasMessage: !!message,
    careTypes: submission.careTypes.join(","),
    role: submission.completedByRole,
  });
}

export interface DentalCheckInSubmission {
  mindId: string;
  responses: { questionKey: string; answer: string }[];
  completedByRole: string;
}

export async function submitDentalCheckIn(
  accountId: string,
  submission: DentalCheckInSubmission
): Promise<void> {
  if (!accountId) {
    throw new Error("Missing accountId for submitDentalCheckIn.");
  }
  if (!submission.mindId) {
    throw new Error("Missing mindId for submitDentalCheckIn.");
  }
  if (submission.responses.length === 0) {
    throw new Error("Dental check-in needs at least one answer.");
  }

  const { user } = useAuthStore.getState();
  const entry = {
    responses: submission.responses,
    // arrayUnion cannot hold a sentinel, so history entries carry an ISO
    // string. `latestCheckIn` is a plain field and gets the server's clock.
    completedAt: new Date().toISOString(),
    completedByRole: submission.completedByRole,
  };

  await updateDoc(doc(db, "Account", accountId, "Mind", submission.mindId), {
    // Append-only: the series is the feature, so entries are never rewritten.
    "dentalAssessment.checkInHistory": arrayUnion(entry),
    "dentalAssessment.latestCheckIn": {
      ...entry,
      completedAt: serverTimestamp(),
    },
    "dentalAssessment.updatedAt": serverTimestamp(),
  });

  logEvent("um_dental_checkin_completed", {
    accountId,
    userId: user?.uid,
    mindId: submission.mindId,
    role: submission.completedByRole,
    // Each answer as its own param, which is what makes the aggregate
    // queryable in analytics rather than a blob nobody can group by.
    ...Object.fromEntries(
      submission.responses.map((r) => [
        r.questionKey.replace(/-/g, "_"),
        r.answer,
      ])
    ),
  });
}

/** True when an activity is the in-app review / store-rating prompt (used to record dismiss + avoid re-enqueue races). */
export function isReviewfeedbackStatsActivity(
  activity: Activity | null | undefined
): boolean {
  if (!activity) return false;
  const name = (activity.name ?? "").toLowerCase();
  const tid = (activity.templateId ?? "").toLowerCase();
  if (
    name.includes("review") ||
    name.includes("rate") ||
    name.includes("feedback") ||
    tid.includes("review") ||
    tid.includes("feedback") ||
    tid.includes("rate")
  ) {
    return true;
  }
  const payload = activity.dynamicContent?.payload;
  if (
    !payload ||
    typeof payload !== "object" ||
    !("actions" in payload) ||
    !Array.isArray((payload as { actions: unknown }).actions)
  ) {
    return false;
  }
  const actions = (payload as { actions: { wellKnownActionId?: string }[] })
    .actions;
  return actions.some((a) => a.wellKnownActionId === "rateApp");
}

/**
 * Mark feedback popup as dismissed (Maybe later). Next show after cooldown (stored on Settings/account).
 * Patches auth store optimistically so eligibility checks see the cooldown immediately (avoids re-queue races).
 */
export async function markFeedbackDismissed(accountId: string): Promise<void> {
  const settingsRef = getAccountSettingsFeedbackRef(accountId);
  try {
    await updateDoc(settingsRef, {
      "feedbackStats.feedbackMaybeLaterAt": serverTimestamp(),
    });
  } catch (e) {
    try {
      const snap = await getDoc(settingsRef);
      const existing = snap.data() as AccountSettings | undefined;
      await setDoc(
        settingsRef,
        {
          feedbackStats: {
            ...(existing?.feedbackStats || {}),
            feedbackMaybeLaterAt: serverTimestamp(),
          },
        },
        { merge: true }
      );
    } catch (e2) {
      console.warn("[feedback] Failed to record feedbackMaybeLaterAt", e2);
      return;
    }
  }

  const { accountId: activeAccountId, accountSettings } =
    useAuthStore.getState();
  if (activeAccountId !== accountId) return;

  useAuthStore.setState({
    accountSettings: {
      ...(accountSettings || {}),
      feedbackStats: {
        ...(accountSettings?.feedbackStats || {}),
        feedbackMaybeLaterAt: Timestamp.now(),
      },
    },
  });
}

/**
 * All conditions for when the feedback popup is shown. Every condition must pass.
 *
 * Gates use `Account/{id}/Settings/account.feedbackStats` plus engagement points on the account doc.
 */
export function shouldShowFeedbackPopup(context: FeedbackShowContext): boolean {
  return shouldEnqueueFeedbackReviewActivity(context);
}

/** In-memory only: prevents duplicate concurrent enqueue for the same account in one tab. */
const inflightReviewEnqueueKeys = new Set<string>();

const ON_APP_OPEN_SCHEDULE_DELAY_MS = 2500;

/** One pending on-app-open timer per account (avoids duplicate fires when React re-runs the effect). */
const pendingOnAppOpenTimers = new Map<string, number>();

/**
 * Whether we should run the delayed on-app-open review check.
 * Missing / unrecognized FeedbackStrategy values return false so no review trigger is scheduled.
 * Parsed `after_activity` variants also return false so we do not schedule an on-app-open timer.
 */
export function shouldScheduleFeedbackOnAppOpen(): boolean {
  const variant = getFeedbackVariantConfig(getFeedbackExperimentVariant());
  if (!variant) return false;
  return variant.trigger === "on_app_open";
}

export type ScheduleOnAppOpenFeedbackOptions = {
  accountId: string;
  getAccount: () => Account | null | undefined;
  /** Current `Account/{id}/Settings/account` data (for `feedbackStats` eligibility). */
  getAccountSettings: () => AccountSettings | null | undefined;
  getPathname: () => string;
  delayMs?: number;
};

/**
 * App lifecycle: after auth is ready, waits briefly then — only if the user’s FeedbackStrategy
 * variant uses `on_app_open` — evaluates route and may queue the review job (user-targeted; no mind id).
 */
export function scheduleOnAppOpenFeedbackReviewIfApplicable(
  options: ScheduleOnAppOpenFeedbackOptions
): () => void {
  const {
    accountId,
    getAccount,
    getAccountSettings,
    getPathname,
    delayMs = ON_APP_OPEN_SCHEDULE_DELAY_MS,
  } = options;

  if (!shouldScheduleFeedbackOnAppOpen()) {
    return () => {};
  }

  const scheduleKey = accountId;
  const previousTimerId = pendingOnAppOpenTimers.get(scheduleKey);
  if (previousTimerId !== undefined) {
    window.clearTimeout(previousTimerId);
    pendingOnAppOpenTimers.delete(scheduleKey);
  }

  let cancelled = false;
  const timerId = window.setTimeout(() => {
    pendingOnAppOpenTimers.delete(scheduleKey);
    if (cancelled) return;

    const pathname = getPathname();
    const account = getAccount();
    const accountSettings = getAccountSettings();

    if (pathname.startsWith("/auth") || pathname.startsWith("/onboarding")) {
      return;
    }

    shouldSendReview({
      accountId,
      account: account ?? undefined,
      accountSettings: accountSettings ?? undefined,
      triggerSource: "on_app_open",
    });
  }, delayMs);

  pendingOnAppOpenTimers.set(scheduleKey, timerId);

  return () => {
    cancelled = true;
    window.clearTimeout(timerId);
    if (pendingOnAppOpenTimers.get(scheduleKey) === timerId) {
      pendingOnAppOpenTimers.delete(scheduleKey);
    }
  };
}

/**
 * When Remote Config / engagement / local gates pass (same as the former popup),
 * queues the createReviewRequest job so the review activity appears in the app.
 *
 * **Enqueue decision** — including `on_app_open` — uses only
 * {@link getFeedbackPopupEligibilityBreakdown} via {@link getFeedbackReviewQueueBreakdown}
 * (same checks as the Danger Zone UI).
 */
export function shouldSendReview(options: {
  accountId: string;
  account: Account | null | undefined;
  accountSettings?: AccountSettings | null;
  triggerSource: FeedbackTrigger;
}): void {
  const { accountId, account, accountSettings, triggerSource } = options;
  const breakdown = getFeedbackReviewQueueBreakdown(
    accountId,
    account,
    triggerSource,
    accountSettings
  );
  if (!breakdown.wouldAskForReview) {
    return;
  }

  const inflightKey = accountId;
  if (inflightReviewEnqueueKeys.has(inflightKey)) {
    return;
  }
  inflightReviewEnqueueKeys.add(inflightKey);

  enqueueReviewRequestJob(accountId)
    .catch((error) => {
      console.warn("Failed to queue createReviewRequest job", error);
    })
    .finally(() => {
      inflightReviewEnqueueKeys.delete(inflightKey);
    });
}

export type FeedbackEligibilityCheck = {
  id: string;
  ok: boolean;
  label: string;
  detail?: string;
};

export type FeedbackPopupEligibilityBreakdown = {
  wouldAskForReview: boolean;
  /** Raw Remote Config "FeedbackStrategy" value (after defaults / env override). */
  feedbackStrategyRaw: string | null;
  variantConfig: FeedbackVariantConfig | null;
  effectivePointsThreshold: number;
  checks: FeedbackEligibilityCheck[];
};

export type FeedbackEligibilityBreakdownOptions = {
  /**
   * When true and there is no parsed FeedbackStrategy variant, skip the
   * "strategy configured" and "trigger matches variant" gates (e.g. owner manual queue).
   * If a variant exists, behavior is unchanged.
   */
  relaxMissingFeedbackStrategy?: boolean;
};

/**
 * Per-account diagnostics and **the only gate** for queueing `createReviewRequest`.
 * Reads prompt state from `context.accountSettings?.feedbackStats`.
 */
export function getFeedbackPopupEligibilityBreakdown(
  context: FeedbackShowContext,
  options?: FeedbackEligibilityBreakdownOptions
): FeedbackPopupEligibilityBreakdown {
  const {
    accountId,
    accountSettings,
    totalPoints,
    accountType,
    accountCreatedAt,
    triggerSource,
    variantConfig,
  } = context;

  const prompt = getfeedbackStatsState(accountSettings ?? undefined);
  const feedbackStrategyRaw = getFeedbackExperimentVariant();
  const effectivePointsThreshold =
    variantConfig?.pointsThreshold ?? FEEDBACK_POINTS_THRESHOLD_DEFAULT;

  const checks: FeedbackEligibilityCheck[] = [];

  checks.push({
    id: "account",
    ok: !!accountId,
    label: "Account present",
    detail: accountId ?? "—",
  });

  const showCount = prompt?.reviewPromptShowCount ?? 0;
  checks.push({
    id: "show_limit",
    ok: !!accountId && showCount < FEEDBACK_MAX_SHOW_COUNT,
    label: "Popup show count under max",
    detail: accountId
      ? `${showCount} / ${FEEDBACK_MAX_SHOW_COUNT} (settings)`
      : "—",
  });

  checks.push({
    id: "points",
    ok: totalPoints >= effectivePointsThreshold,
    label: "Engagement points ≥ threshold",
    detail: `${totalPoints} / ${effectivePointsThreshold}`,
  });

  const strategyConfigured = !!variantConfig;
  const strategyRelaxed =
    !!options?.relaxMissingFeedbackStrategy && !variantConfig;
  checks.push({
    id: "strategy_configured",
    ok: strategyConfigured || strategyRelaxed,
    label: "Feedback strategy configured",
    detail: strategyConfigured
      ? "Parsed feedback strategy variant"
      : strategyRelaxed
        ? "Skipped (manual queue; no FeedbackStrategy)"
        : "Missing or unrecognized FeedbackStrategy (review enqueue disabled)",
  });

  const triggerOk = !!variantConfig && variantConfig.trigger === triggerSource;
  const triggerRelaxed =
    !!options?.relaxMissingFeedbackStrategy && !variantConfig;
  checks.push({
    id: "trigger",
    ok: triggerOk || triggerRelaxed,
    label: "Trigger matches variant",
    detail: variantConfig
      ? `variant wants "${variantConfig.trigger}", evaluating as "${triggerSource}"`
      : triggerRelaxed
        ? "Skipped (manual queue; no variant)"
        : "No parsed variant (trigger disabled)",
  });

  let weekOk = true;
  let weekDetail = "—";
  if (variantConfig?.requireOneWeek) {
    if (!accountCreatedAt?.toDate) {
      weekOk = false;
      weekDetail = "Account created date missing";
    } else {
      const createdMs = accountCreatedAt.toDate().getTime();
      const minAgeMs = FEEDBACK_MIN_AGE_DAYS_FOR_1WEEK * 24 * 60 * 60 * 1000;
      const ageDays = (Date.now() - createdMs) / (24 * 60 * 60 * 1000);
      weekOk = Date.now() - createdMs >= minAgeMs;
      weekDetail = `${ageDays.toFixed(1)} d old (need ≥ ${FEEDBACK_MIN_AGE_DAYS_FOR_1WEEK} d)`;
    }
  } else {
    weekDetail = variantConfig
      ? "Variant does not require 1 week"
      : "N/A (no parsed variant)";
  }
  checks.push({
    id: "account_age",
    ok: weekOk,
    label: "Account old enough (1-week variants)",
    detail: weekDetail,
  });

  const excluded =
    !!accountType &&
    FEEDBACK_EXCLUDED_ACCOUNT_TYPES.includes(
      accountType as (typeof FEEDBACK_EXCLUDED_ACCOUNT_TYPES)[number]
    );
  checks.push({
    id: "account_type",
    ok: !excluded,
    label: "Account type not excluded",
    detail: accountType ?? "—",
  });

  const submittedBlocks = !!prompt?.feedbackSubmittedAt;
  const cooldownMs = FEEDBACK_DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

  let dismissOk = true;
  let dismissDetail = "—";
  const maybeLaterMs = timestampMs(prompt?.feedbackMaybeLaterAt);
  if (maybeLaterMs == null) {
    dismissDetail = "Never dismissed (Maybe later)";
  } else {
    const elapsed = Date.now() - maybeLaterMs;
    dismissOk = elapsed > cooldownMs;
    dismissDetail = dismissOk
      ? `"Maybe later" cooldown passed (${FEEDBACK_DISMISS_COOLDOWN_DAYS}d)`
      : `Cooldown: ~${Math.max(0, Math.ceil((cooldownMs - elapsed) / (24 * 60 * 60 * 1000)))} day(s) left`;
  }

  let reviewActivitySentOk = true;
  let reviewActivitySentDetail = "—";
  const lastQueuedMs = timestampMs(prompt?.lastReviewActivityQueued);
  if (lastQueuedMs == null) {
    reviewActivitySentDetail = "No review activity queued yet (settings)";
  } else {
    const elapsed = Date.now() - lastQueuedMs;
    reviewActivitySentOk = elapsed > cooldownMs;
    reviewActivitySentDetail = reviewActivitySentOk
      ? `Last review activity queued ≥ ${FEEDBACK_DISMISS_COOLDOWN_DAYS}d ago`
      : `Review activity cooldown: ~${Math.max(0, Math.ceil((cooldownMs - elapsed) / (24 * 60 * 60 * 1000)))} day(s) left`;
  }

  checks.push({
    id: "not_submitted",
    ok: !submittedBlocks,
    label: "Has not submitted feedback (settings)",
    detail: submittedBlocks ? "Blocked" : "OK",
  });

  checks.push({
    id: "dismiss_cooldown",
    ok: dismissOk,
    label: "Dismiss (Maybe later) cooldown clear",
    detail: dismissDetail,
  });

  checks.push({
    id: "review_activity_cooldown",
    ok: reviewActivitySentOk,
    label: "Review-request activity send cooldown clear",
    detail: reviewActivitySentDetail,
  });

  const wouldAskForReview = checks.every((c) => c.ok);

  return {
    wouldAskForReview,
    feedbackStrategyRaw,
    variantConfig: variantConfig ?? null,
    effectivePointsThreshold,
    checks,
  };
}

/**
 * Whether we may enqueue a review-request job — same boolean as
 * `getFeedbackPopupEligibilityBreakdown(...).wouldAskForReview`.
 */
export function shouldEnqueueFeedbackReviewActivity(
  context: FeedbackShowContext
): boolean {
  return getFeedbackPopupEligibilityBreakdown(context).wouldAskForReview;
}

/**
 * Builds {@link FeedbackShowContext} from account state and runs
 * {@link getFeedbackPopupEligibilityBreakdown}. Use this anywhere you need the same
 * decision as `shouldSendReview` (e.g. on_app_open timer → enqueue).
 */
export function getFeedbackReviewQueueBreakdown(
  accountId: string | null,
  account: Account | null | undefined,
  triggerSource: FeedbackTrigger,
  accountSettings?: AccountSettings | null,
  breakdownOptions?: FeedbackEligibilityBreakdownOptions
): FeedbackPopupEligibilityBreakdown {
  const ctx = buildFeedbackShowContext(
    accountId,
    account,
    triggerSource,
    accountSettings
  );
  return getFeedbackPopupEligibilityBreakdown(ctx, breakdownOptions);
}

export type UseOnAppOpenFeedbackScheduleParams = {
  accountId: string | null;
  account: Account | null | undefined;
  accountSettings: AccountSettings | null | undefined;
  initializing: boolean;
  /** Current pathname (e.g. `location.pathname`). */
  locationPathname: string;
  role: AccountRole | undefined;
};

/**
 * React subscription for {@link scheduleOnAppOpenFeedbackReviewIfApplicable}.
 * Keeps all on-app-open feedback wiring out of `App.tsx`.
 */
export function useOnAppOpenFeedbackSchedule(
  params: UseOnAppOpenFeedbackScheduleParams
): void {
  const {
    accountId,
    account,
    accountSettings,
    initializing,
    locationPathname,
    role,
  } = params;

  const locationRef = useRef(locationPathname);
  useEffect(() => {
    locationRef.current = locationPathname;
  }, [locationPathname]);

  const accountRef = useRef(account);
  const accountSettingsRef = useRef(accountSettings);

  useEffect(() => {
    accountRef.current = account;
  }, [account]);

  useEffect(() => {
    accountSettingsRef.current = accountSettings;
  }, [accountSettings]);

  useEffect(() => {
    if (!accountId || initializing) return;

    return scheduleOnAppOpenFeedbackReviewIfApplicable({
      accountId,
      getAccount: () => accountRef.current,
      getAccountSettings: () => accountSettingsRef.current,
      getPathname: () => locationRef.current,
    });
  }, [accountId, initializing, role]);
}
